import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../../authorization";
import { type Schema, zql } from "../../generatedDrizzleSchema";
import {
  type CrossResourceAvailabilityRuleId,
  crossResourceRule,
  dependentRequirementRank,
  firstUnmetSupplierRequirement,
  isSupplierAvailable,
  type PrincipalKind,
  principalKindOf,
  principalsWithPermission,
  type RbacReader,
  requiredSupplierPermissions,
  resolveSupplierName,
  zqlGrantReader,
} from "../crossResourceAvailability";
import type { GrantLike } from "../roles";

export type DependentPrincipalGrant = GrantLike & {
  userGroupId: string | null;
  userId: string | null;
  serviceAccountId?: string | null;
};

type ZqlTx = Pick<Transaction<Schema>, "run">;

async function supplierIdsUsedByDependent(
  grantReader: RbacReader,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentId: string
): Promise<Set<string>> {
  const rule = crossResourceRule(ruleId);
  if (!rule.usageEdges) return new Set();
  const edges = await rule.usageEdges(grantReader, { dependentId });
  return new Set(edges.map((e) => e.supplierId));
}

// Networks have no `usageEdges`; a space "uses" the networks it currently
// reaches, so adding a writer who lacks one is blocked rather than silently
// stripping it. With no writer yet nothing is reachable, so nothing is guarded.
async function reachableNetworkIdsForSpace(
  tx: ZqlTx,
  tenantId: string,
  spaceId: string
): Promise<Set<string>> {
  const spaceGrants = await zqlGrantReader(tx, tenantId).grantsForScope(
    spaceId
  );
  const writers = principalsWithPermission(spaceGrants, "space:write");
  if (
    writers.userIds.size === 0 &&
    writers.groupIds.size === 0 &&
    writers.serviceAccountIds.size === 0
  )
    return new Set();
  const networks = await tx.run(zql.networks.where("disabledAt", "IS", null));
  const reachable = new Set<string>();
  for (const network of networks) {
    if (
      await isSupplierAvailable({
        tx,
        tenantId,
        ruleId: "space_uses_networks",
        dependentScopeId: spaceId,
        supplierScopeId: network.id,
      })
    )
      reachable.add(network.id);
  }
  return reachable;
}

function principalKey(g: {
  userGroupId?: string | null;
  userId?: string | null;
  serviceAccountId?: string | null;
}): string | null {
  return g.userGroupId
    ? `g:${g.userGroupId}`
    : g.userId
      ? `u:${g.userId}`
      : g.serviceAccountId
        ? `s:${g.serviceAccountId}`
        : null;
}

// A downgrade (editor -> viewer) needs no re-validation since use subsumes read.
export function principalsCrossingIntoRequirement(
  ruleId: CrossResourceAvailabilityRuleId,
  existing: readonly DependentPrincipalGrant[],
  desired: readonly DependentPrincipalGrant[]
): DependentPrincipalGrant[] {
  const previousRank = new Map<string, number>();
  for (const row of existing) {
    const key = principalKey(row);
    if (!key) continue;
    const rank = dependentRequirementRank(ruleId, row);
    const current = previousRank.get(key);
    if (current === undefined || rank < current) previousRank.set(key, rank);
  }
  return desired.filter((g) => {
    const key = principalKey(g);
    if (key == null) return false;
    const rank = dependentRequirementRank(ruleId, g);
    if (rank === Number.POSITIVE_INFINITY) return false;
    return rank < (previousRank.get(key) ?? Number.POSITIVE_INFINITY);
  });
}

export async function assertDependentPrincipalsMeetRequirements(
  tx: ZqlTx,
  tenantId: string,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentId: string,
  addedGrants: readonly DependentPrincipalGrant[]
): Promise<void> {
  if (addedGrants.length === 0) return;

  // Only grants whose tier actually requires supplier access need checking.
  const relevant = addedGrants.filter(
    (g) => requiredSupplierPermissions(ruleId, g).length > 0
  );
  if (relevant.length === 0) return;

  const grantReader = zqlGrantReader(tx, tenantId);
  const supplierIds =
    ruleId === "space_uses_networks"
      ? await reachableNetworkIdsForSpace(tx, tenantId, dependentId)
      : await supplierIdsUsedByDependent(grantReader, ruleId, dependentId);
  if (supplierIds.size === 0) return;

  // Resolve each added user's group memberships once and reuse across every supplier.
  const membershipsByUser = new Map<string, readonly string[]>();
  for (const g of relevant) {
    if (g.userId && !membershipsByUser.has(g.userId)) {
      const userGrants = await grantReader.grantsForPrincipal({
        userId: g.userId,
      });
      membershipsByUser.set(
        g.userId,
        userGrants.map((ug) => ug.scopeId)
      );
    }
  }

  const unmetNames: string[] = [];
  let unmetKind: PrincipalKind | null = null;
  for (const supplierId of supplierIds) {
    const supplierGrants = await grantReader.grantsForScope(supplierId);
    const unmet = firstUnmetSupplierRequirement(
      ruleId,
      relevant,
      supplierGrants,
      membershipsByUser
    );
    if (!unmet) continue;
    unmetKind ??= principalKindOf(unmet.grant);
    unmetNames.push(await resolveSupplierName(tx, ruleId, supplierId));
  }
  if (unmetNames.length > 0) {
    unmetNames.sort();
    throw userFacingError(
      crossResourceRule(ruleId).messages.principalLacksAccess(
        unmetKind!,
        unmetNames
      )
    );
  }
}
